import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, MessageCircle, Phone, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT, DESTINATIONS, TOURS, VEHICLES, EXPERIENCE_TYPES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-forest-950/70 border border-gold/15 focus:border-gold/60 text-sm text-cream placeholder:text-fog px-4 py-3 focus:outline-none transition-colors [color-scheme:dark]";

const Field = ({ label, children, span }) => (
  <label className={`flex flex-col gap-2 ${span ? "sm:col-span-2" : ""}`}>
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold/80">{label}</span>
    {children}
  </label>
);

const DESTINATION_OPTIONS = [
  ...TOURS.map((t) => t.name),
  ...DESTINATIONS.map((d) => d.name),
];

const VEHICLE_OPTIONS = [...VEHICLES.cars, ...VEHICLES.bikes].map((v) => v.name);

export const EnquiryModal = ({ open, prefill, onClose }) => {
  const [form, setForm] = useState({
    full_name: "", mobile: "", email: "", destination: "",
    travel_date: "", travellers: "", vehicle: "", experience: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setDone(false);
      setForm((f) => ({
        ...f,
        destination: prefill.destination || "",
        travel_date: prefill.travel_date || "",
        travellers: prefill.travellers || "",
        vehicle: prefill.vehicle || "",
        experience: prefill.experience || "",
      }));
    }
  }, [open, prefill]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.mobile.trim() || !form.email.trim()) {
      toast.error("Please fill your name, mobile number and email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
    } catch (err) {
      toast.error("Could not send your enquiry. Please WhatsApp or call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="enquiry-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
        >
          <div className="absolute inset-0 bg-forest-950/85 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full sm:max-w-xl glass-panel shadow-2xl shadow-black/70 max-h-[92vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between px-7 py-5 border-b border-gold/15 sticky top-0 bg-forest-900/90 backdrop-blur-xl z-10">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold block">
                  HiddenBiomes
                </span>
                <h3 className="font-serif text-2xl text-cream mt-1">
                  {done ? "Enquiry Received" : "Plan Your Journey"}
                </h3>
              </div>
              <button
                data-testid="enquiry-modal-close-button"
                onClick={onClose}
                className="text-cream/60 hover:text-gold transition-colors p-2"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {done ? (
              <div data-testid="enquiry-success" className="px-7 py-12 text-center">
                <CheckCircle2 size={52} className="text-gold mx-auto" strokeWidth={1.2} />
                <p className="font-serif text-2xl text-cream mt-6 leading-snug">
                  Thank you for contacting HIDDENBIOMES.
                </p>
                <p className="text-sm text-sage mt-3 max-w-sm mx-auto leading-relaxed">
                  Our travel team will contact you shortly to plan your journey.
                </p>
                <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href={CONTACT.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="enquiry-whatsapp-btn"
                    className="flex items-center gap-2 bg-gold text-forest-950 font-mono text-[11px] uppercase tracking-[0.2em] px-7 py-3.5 hover:bg-cream transition-colors"
                  >
                    <MessageCircle size={14} /> WhatsApp Us
                  </a>
                  <a
                    href={`tel:${CONTACT.phoneRaw}`}
                    data-testid="enquiry-call-btn"
                    className="flex items-center gap-2 border border-gold/40 text-gold font-mono text-[11px] uppercase tracking-[0.2em] px-7 py-3.5 hover:bg-gold hover:text-forest-950 transition-colors"
                  >
                    <Phone size={14} /> Call Us
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="px-7 py-7 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="enquiry-form">
                <Field label="Full Name *">
                  <input
                    data-testid="enquiry-name-input"
                    value={form.full_name}
                    onChange={set("full_name")}
                    placeholder="Your name"
                    className={inputCls}
                    required
                  />
                </Field>
                <Field label="Mobile Number *">
                  <input
                    data-testid="enquiry-mobile-input"
                    value={form.mobile}
                    onChange={set("mobile")}
                    placeholder="+91"
                    type="tel"
                    className={inputCls}
                    required
                  />
                </Field>
                <Field label="Email Address *" span>
                  <input
                    data-testid="enquiry-email-input"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    type="email"
                    className={inputCls}
                    required
                  />
                </Field>
                <Field label="Destination / Tour">
                  <select data-testid="enquiry-destination-select" value={form.destination} onChange={set("destination")} className={inputCls}>
                    <option value="" className="bg-forest-900">Not sure yet</option>
                    {DESTINATION_OPTIONS.map((d) => (
                      <option key={d} value={d} className="bg-forest-900">{d}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Travel Date">
                  <input data-testid="enquiry-date-input" type="date" value={form.travel_date} onChange={set("travel_date")} className={inputCls} />
                </Field>
                <Field label="Number of Travellers">
                  <select data-testid="enquiry-travellers-select" value={form.travellers} onChange={set("travellers")} className={inputCls}>
                    <option value="" className="bg-forest-900">Select</option>
                    {["1–2", "3–4", "5–8", "9+"].map((t) => (
                      <option key={t} value={t} className="bg-forest-900">{t}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Preferred Vehicle">
                  <select data-testid="enquiry-vehicle-select" value={form.vehicle} onChange={set("vehicle")} className={inputCls}>
                    <option value="" className="bg-forest-900">Not required</option>
                    {VEHICLE_OPTIONS.map((v) => (
                      <option key={v} value={v} className="bg-forest-900">{v}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Experience Type" span>
                  <select data-testid="enquiry-experience-select" value={form.experience} onChange={set("experience")} className={inputCls}>
                    <option value="" className="bg-forest-900">Choose an experience</option>
                    {EXPERIENCE_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-forest-900">{t}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Message / Special Requirements" span>
                  <textarea
                    data-testid="enquiry-message-input"
                    value={form.message}
                    onChange={set("message")}
                    rows={3}
                    placeholder="Tell us about the journey you have in mind…"
                    className={`${inputCls} resize-none`}
                  />
                </Field>
                <div className="sm:col-span-2 pt-2">
                  <button
                    data-testid="submit-enquiry-button"
                    type="submit"
                    disabled={sending}
                    className="w-full bg-gold text-forest-950 font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-cream transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-3"
                  >
                    {sending && <Loader2 size={14} className="animate-spin" />}
                    {sending ? "Sending…" : "Submit Enquiry"}
                  </button>
                  <p className="text-center font-mono text-[10px] tracking-[0.15em] text-fog mt-4">
                    No payment now — our team confirms every detail personally.
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
